import styled from "styled-components"

import { ChevronLeftIcon, ChevronRightIcon } from "@minuthq/meatball-ui-icons"
import { Button, IconButton, spacing, Text } from "@minuthq/meatball-ui-react"

import { useTranslate } from "src/i18n/useTranslate"
import {
  DropdownSelect,
  TSelectOption,
} from "src/ui/DropdownSelect/DropdownSelect"
import {
  TSchedulerViewType,
  WeekStartsOn,
} from "src/ui/Scheduler/schedulerTypes"
import { ZonedDateTime } from "src/utils/ZonedDateTime"

type RangeNavigationProps = {
  onPrevious: () => void
  onNext: () => void
  onToday: () => void
  label?: string
  viewType: TSchedulerViewType
  setViewType: (viewType: TSchedulerViewType) => void
  setDateRangeStart: (date: ZonedDateTime) => void
  now: ZonedDateTime
  weekStartsOn: WeekStartsOn
}

export function RangeNavigation({
  onPrevious,
  onNext,
  onToday,
  label,
  viewType,
  setViewType,
  setDateRangeStart,
  now,
  weekStartsOn,
}: RangeNavigationProps) {
  const { t, langKeys } = useTranslate()

  const rangeTypeOptions: TSelectOption<TSchedulerViewType>[] = [
    {
      label: t(langKeys.week),
      value: "week",
    },
    {
      label: t(langKeys.month),
      value: "month",
    },
    {
      label: t(langKeys.year),
      value: "year",
    },
  ]

  function handleViewChange({
    option,
  }: {
    option: TSelectOption<TSchedulerViewType>
  }) {
    const viewDateMap = {
      week: now.atStartOfWeek({ weekStartsOn }),
      month: now.atStartOfMonth(),
      year: now.atStartOfYear(),
    }

    const newViewType = option.value
    setViewType(newViewType)
    setDateRangeStart(viewDateMap[newViewType])
  }

  function getDisplayLabel() {
    const currentOption = rangeTypeOptions.find(
      (option) => option.value === viewType
    )
    return currentOption?.label || t(langKeys.week)
  }

  return (
    <NavigationContainer>
      <DropdownSelect
        label={getDisplayLabel()}
        options={rangeTypeOptions}
        selectedValue={viewType}
        onChange={({ option }) => handleViewChange({ option })}
        mButtonVariant="subtle"
        size="small"
      />

      <IconButton
        Icon={ChevronLeftIcon}
        onClick={onPrevious}
        size="small"
        variant="subtle"
        aria-label="Previous week"
      />

      <Text variant="subtitleS">{label || t(langKeys.current_week)}</Text>

      <IconButton
        Icon={ChevronRightIcon}
        onClick={onNext}
        size="small"
        variant="subtle"
        aria-label="Next week"
      />
      <Button size="small" variant="subtle" onClick={onToday}>
        {t(langKeys.today)}
      </Button>
    </NavigationContainer>
  )
}

const NavigationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.M};
`
